import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useWebhookOperation } from "./utils/webhookUtils";
import WebhookStatusDisplay from "./utils/WebhookStatusDisplay";
import { apiFetch } from "./config/apiConfig";

const RssManagementPage = ({ user, logout }) => {
  const navigate = useNavigate();

  // Webhook operation hooks
  const rssOperation = useWebhookOperation();
  const deduplicationOperation = useWebhookOperation();
  const cleanupOperation = useWebhookOperation();

  // State for RSS management
  const [rssConfig, setRssConfig] = useState(null);
  const [rssConfigLoading, setRssConfigLoading] = useState(false);
  const [rssConfigEditing, setRssConfigEditing] = useState(false);
  const [editingRssConfig, setEditingRssConfig] = useState(null);
  const [triggeringRssUpdate, setTriggeringRssUpdate] = useState(false);

  // State for deduplication
  const [triggeringDeduplication, setTriggeringDeduplication] = useState(false);

  // State for pagination and filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [biasFilter, setBiasFilter] = useState("all");
  const [enabledFilter, setEnabledFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [feedsPerPage, setFeedsPerPage] = useState(50);

  // Filter and paginate feeds
  const filteredFeeds = useMemo(() => {
    if (!rssConfig?.feeds) return [];

    return rssConfig.feeds.filter((feed) => {
      // Skip null or undefined feeds
      if (!feed) return false;

      const matchesSearch =
        !searchTerm ||
        (feed.name &&
          feed.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (feed.url && feed.url.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesBias = biasFilter === "all" || feed.bias === biasFilter;

      const matchesEnabled =
        enabledFilter === "all" ||
        (enabledFilter === "enabled" && feed.enabled) ||
        (enabledFilter === "disabled" && !feed.enabled);

      return matchesSearch && matchesBias && matchesEnabled;
    });
  }, [rssConfig?.feeds, searchTerm, biasFilter, enabledFilter]);

  const paginatedFeeds = useMemo(() => {
    const startIndex = (currentPage - 1) * feedsPerPage;
    return filteredFeeds.slice(startIndex, startIndex + feedsPerPage);
  }, [filteredFeeds, currentPage, feedsPerPage]);

  const totalPages = Math.ceil(filteredFeeds.length / feedsPerPage);

  const ensureAdminSession = useCallback(async () => {
    try {
      const sessionToken = localStorage.getItem("sessionToken");
      if (!sessionToken) {
        logout();
        return null;
      }

      const response = await apiFetch("/auth/validate", {
        method: "POST",
        body: JSON.stringify({ sessionToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.valid && data.user?.isAdmin) {
          return sessionToken;
        } else {
          logout();
          return null;
        }
      } else {
        logout();
        return null;
      }
    } catch (error) {
      logout();
      return null;
    }
  }, [logout]);

  // RSS Management Functions
  const loadRssConfig = useCallback(async () => {
    setRssConfigLoading(true);
    try {
      const sessionToken = await ensureAdminSession();
      const response = await apiFetch("/admin/rss-config");

      if (response.ok) {
        const data = await response.json();
        setRssConfig(data);
      } else if (response.status === 403) {
        // Try creating a new session
        localStorage.removeItem("sessionToken");
        const newSessionToken = await ensureAdminSession();
        if (newSessionToken) {
          // Retry with new token
          const retryResponse = await apiFetch("/admin/rss-config");
          if (retryResponse.ok) {
            const data = await retryResponse.json();
            setRssConfig(data);
          }
        }
      }
    } catch (error) {
      // Error loading RSS config - silent fail
    } finally {
      setRssConfigLoading(false);
    }
  }, [ensureAdminSession]);

  useEffect(() => {
    if (user?.isAdmin) {
      loadRssConfig();
    }
  }, [user, loadRssConfig]);

  const saveRssConfig = async () => {
    if (!editingRssConfig) return;

    setRssConfigLoading(true);
    try {
      const sessionToken = await ensureAdminSession();
      const response = await apiFetch("/admin/rss-config", {
        method: "POST",
        body: JSON.stringify(editingRssConfig),
      });

      if (response.ok) {
        await loadRssConfig();
        setRssConfigEditing(false);
        setEditingRssConfig(null);
      }
    } catch (error) {
      // Error saving RSS config - silent fail
    } finally {
      setRssConfigLoading(false);
    }
  };

  const startEditingRssConfig = () => {
    setEditingRssConfig(JSON.parse(JSON.stringify(rssConfig)));
    setRssConfigEditing(true);
  };

  const cancelEditingRssConfig = () => {
    setRssConfigEditing(false);
    setEditingRssConfig(null);
  };

  const updateRssConfigField = (field, value) => {
    setEditingRssConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateRssFeed = (index, field, value) => {
    setEditingRssConfig((prev) => ({
      ...prev,
      feeds: prev.feeds.map((feed, i) =>
        i === index ? { ...feed, [field]: value } : feed
      ),
    }));
  };

  const addRssFeed = () => {
    setEditingRssConfig((prev) => ({
      ...prev,
      feeds: [
        ...prev.feeds,
        {
          name: "",
          url: "",
          bias: "center",
          enabled: true,
          max_articles: 10,
        },
      ],
    }));
  };

  const removeRssFeed = (index) => {
    setEditingRssConfig((prev) => ({
      ...prev,
      feeds: prev.feeds.filter((_, i) => i !== index),
    }));
  };

  const triggerRssUpdate = async () => {
    setTriggeringRssUpdate(true);
    try {
      // Execute RSS aggregation with webhook support (will auto-get API key)
      await rssOperation.executeRSSAggregation(null, true);

      // Reload data after completion (either sync or async)
      if (!rssOperation.isRunning) {
        await loadRssConfig();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error triggering RSS update:", error);
    } finally {
      setTriggeringRssUpdate(false);
    }
  };

  const triggerDeduplication = async () => {
    setTriggeringDeduplication(true);
    try {
      // Execute story deduplication with webhook support (will auto-get API key)
      await deduplicationOperation.executeStoryDeduplication(null, true);

      // Reload data after completion (either sync or async)
      if (!deduplicationOperation.isRunning) {
        await loadRssConfig();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error triggering deduplication:", error);
    } finally {
      setTriggeringDeduplication(false);
    }
  };

  const triggerCleanupOrphanedStories = async () => {
    try {
      // Execute cleanup orphaned stories with webhook support (will auto-get API key)
      await cleanupOperation.executeCleanupOrphanedStories(null, true);

      // Reload data after completion (either sync or async)
      if (!cleanupOperation.isRunning) {
        await loadRssConfig();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error triggering cleanup:", error);
    }
  };

  // Check if user is admin
  if (!user?.isAdmin) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">
            Access Denied
          </h1>
          <p className="text-neutral-600">
            You need admin privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                RSS Feed Management
              </h1>
              <p className="text-neutral-600 mt-2">
                Manage RSS feed sources, configuration, and updates
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate("/admin")}
                className="inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                ← Back to Admin Dashboard
              </button>
              <button
                onClick={() => navigate("/admin/monitoring")}
                className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                📊 System Monitoring
              </button>
            </div>
          </div>
        </div>

        {/* RSS Feed Management */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-medium text-neutral-900">
              📰 RSS Feed & News Management
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={triggerRssUpdate}
                disabled={triggeringRssUpdate || rssOperation.isRunning}
                className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              >
                {triggeringRssUpdate || rssOperation.isRunning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    {rssOperation.isRunning
                      ? "Running in Background..."
                      : "Starting..."}
                  </>
                ) : (
                  "🔄 RSS Aggregation"
                )}
              </button>
              <button
                onClick={triggerDeduplication}
                disabled={
                  triggeringDeduplication || deduplicationOperation.isRunning
                }
                className="inline-flex items-center px-4 py-2 border border-orange-300 text-sm font-medium rounded text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors disabled:opacity-50"
              >
                {triggeringDeduplication || deduplicationOperation.isRunning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                    {deduplicationOperation.isRunning
                      ? "Running in Background..."
                      : "Starting..."}
                  </>
                ) : (
                  "🧹 Deduplicate Stories"
                )}
              </button>
              <button
                onClick={triggerCleanupOrphanedStories}
                disabled={cleanupOperation.isRunning}
                className="inline-flex items-center px-4 py-2 border border-purple-300 text-sm font-medium rounded text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors disabled:opacity-50"
              >
                {cleanupOperation.isRunning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                    {cleanupOperation.isRunning
                      ? "Running in Background..."
                      : "Starting..."}
                  </>
                ) : (
                  "🗑️ Cleanup Orphaned Stories"
                )}
              </button>
            </div>

            {/* Webhook Status Displays */}
            <WebhookStatusDisplay
              isRunning={rssOperation.isRunning}
              status={rssOperation.status}
              result={rssOperation.result}
              error={rssOperation.error}
              operationName="RSS Aggregation"
              onCancel={rssOperation.cancelOperation}
              onReset={rssOperation.resetState}
            />
            <WebhookStatusDisplay
              isRunning={deduplicationOperation.isRunning}
              status={deduplicationOperation.status}
              result={deduplicationOperation.result}
              error={deduplicationOperation.error}
              operationName="Story Deduplication"
              onCancel={deduplicationOperation.cancelOperation}
              onReset={deduplicationOperation.resetState}
            />
            <WebhookStatusDisplay
              isRunning={cleanupOperation.isRunning}
              status={cleanupOperation.status}
              result={cleanupOperation.result}
              error={cleanupOperation.error}
              operationName="Cleanup Orphaned Stories"
              onCancel={cleanupOperation.cancelOperation}
              onReset={cleanupOperation.resetState}
            />
          </div>

          {!rssConfigEditing && (
            <div className="flex items-center justify-between mb-6 bg-blue-50 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <span className="text-blue-700 font-medium">
                  RSS Configuration
                </span>
                <span className="text-blue-600 text-sm">
                  ({rssConfig?.feeds?.length || 0} total feeds,{" "}
                  {rssConfig?.feeds?.filter((f) => f && f.enabled).length || 0}{" "}
                  active)
                </span>
              </div>
              <button
                onClick={startEditingRssConfig}
                disabled={rssConfigLoading}
                className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              >
                ✏️ Edit Configuration
              </button>
            </div>
          )}

          {rssConfigEditing && (
            <div className="flex items-center justify-between mb-6 bg-orange-50 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <span className="text-orange-700 font-medium">
                  Editing RSS Configuration
                </span>
                <span className="text-orange-600 text-sm">
                  Make your changes and save when ready
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={cancelEditingRssConfig}
                  className="inline-flex items-center px-3 py-2 border border-neutral-300 text-sm font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveRssConfig}
                  disabled={rssConfigLoading}
                  className="inline-flex items-center px-4 py-2 border border-green-300 text-sm font-medium rounded text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50"
                >
                  {rssConfigLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    "💾 Save Changes"
                  )}
                </button>
              </div>
            </div>
          )}

          {rssConfigLoading && !rssConfigEditing ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                <span className="text-lg text-neutral-600">
                  Loading RSS configuration...
                </span>
              </div>
            </div>
          ) : rssConfig ? (
            <div className="space-y-8">
              {/* Global Configuration */}
              <div className="bg-neutral-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-neutral-900 mb-6">
                  Global Settings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Update Frequency (minutes)
                    </label>
                    {rssConfigEditing ? (
                      <input
                        type="number"
                        min="5"
                        max="1440"
                        value={editingRssConfig?.updateFrequency || 60}
                        onChange={(e) =>
                          updateRssConfigField(
                            "updateFrequency",
                            parseInt(e.target.value) || 60
                          )
                        }
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    ) : (
                      <div className="text-sm font-mono bg-white px-3 py-2 border border-neutral-200 rounded">
                        {rssConfig.updateFrequency || 60} min
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Max Total Articles
                    </label>
                    {rssConfigEditing ? (
                      <input
                        type="number"
                        min="10"
                        max="200"
                        value={editingRssConfig?.maxTotalArticles || 50}
                        onChange={(e) =>
                          updateRssConfigField(
                            "maxTotalArticles",
                            parseInt(e.target.value) || 50
                          )
                        }
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    ) : (
                      <div className="text-sm font-mono bg-white px-3 py-2 border border-neutral-200 rounded">
                        {rssConfig.maxTotalArticles || 50}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Max Stories Limit
                    </label>
                    {rssConfigEditing ? (
                      <input
                        type="number"
                        min="100"
                        max="2000"
                        value={editingRssConfig?.maxStories || 500}
                        onChange={(e) =>
                          updateRssConfigField(
                            "maxStories",
                            parseInt(e.target.value) || 500
                          )
                        }
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    ) : (
                      <div className="text-sm font-mono bg-white px-3 py-2 border border-neutral-200 rounded">
                        {rssConfig.maxStories || 500}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Min Articles Per Story
                    </label>
                    {rssConfigEditing ? (
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={editingRssConfig?.minArticlesPerStory || 2}
                        onChange={(e) =>
                          updateRssConfigField(
                            "minArticlesPerStory",
                            parseInt(e.target.value) || 2
                          )
                        }
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    ) : (
                      <div className="text-sm font-mono bg-white px-3 py-2 border border-neutral-200 rounded">
                        {rssConfig.minArticlesPerStory || 2}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Story Swarm Min Articles
                    </label>
                    {rssConfigEditing ? (
                      <input
                        type="number"
                        min="2"
                        max="20"
                        value={editingRssConfig?.storySwarmMinArticles || 2}
                        onChange={(e) =>
                          updateRssConfigField(
                            "storySwarmMinArticles",
                            parseInt(e.target.value) || 2
                          )
                        }
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    ) : (
                      <div className="text-sm font-mono bg-white px-3 py-2 border border-neutral-200 rounded">
                        {rssConfig.storySwarmMinArticles || 2}
                      </div>
                    )}
                    <p className="text-xs text-neutral-500 mt-1">
                      Minimum articles required to show in Story Swarms section
                    </p>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="deduplication"
                      checked={
                        rssConfigEditing
                          ? editingRssConfig?.deduplicationEnabled
                          : rssConfig.deduplicationEnabled
                      }
                      onChange={(e) =>
                        rssConfigEditing &&
                        updateRssConfigField(
                          "deduplicationEnabled",
                          e.target.checked
                        )
                      }
                      disabled={!rssConfigEditing}
                      className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                    />
                    <label
                      htmlFor="deduplication"
                      className="text-sm text-neutral-700"
                    >
                      Enable Deduplication
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="clustering"
                      checked={
                        rssConfigEditing
                          ? editingRssConfig?.clusteringEnabled
                          : rssConfig.clusteringEnabled
                      }
                      onChange={(e) =>
                        rssConfigEditing &&
                        updateRssConfigField(
                          "clusteringEnabled",
                          e.target.checked
                        )
                      }
                      disabled={!rssConfigEditing}
                      className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                    />
                    <label
                      htmlFor="clustering"
                      className="text-sm text-neutral-700"
                    >
                      Enable Story Clustering
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="autoUpdate"
                      checked={
                        rssConfigEditing
                          ? editingRssConfig?.autoUpdateEnabled
                          : rssConfig.autoUpdateEnabled
                      }
                      onChange={(e) =>
                        rssConfigEditing &&
                        updateRssConfigField(
                          "autoUpdateEnabled",
                          e.target.checked
                        )
                      }
                      disabled={!rssConfigEditing}
                      className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                    />
                    <label
                      htmlFor="autoUpdate"
                      className="text-sm text-neutral-700"
                    >
                      Enable Auto-Update (Cron)
                    </label>
                  </div>
                </div>
              </div>

              {/* RSS Feeds Table */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-neutral-900">
                    RSS Feed Sources ({rssConfig.feeds?.length || 0} total
                    feeds)
                  </h3>
                  {rssConfigEditing && (
                    <button
                      onClick={addRssFeed}
                      className="inline-flex items-center px-4 py-2 border border-primary-300 text-sm font-medium rounded text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    >
                      + Add Feed
                    </button>
                  )}
                </div>

                {/* Search and Filter Controls */}
                {!rssConfigEditing && (
                  <div className="bg-neutral-50 rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Search Feeds
                        </label>
                        <input
                          type="text"
                          placeholder="Search by name or URL..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1); // Reset to first page when searching
                          }}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Filter by Bias
                        </label>
                        <select
                          value={biasFilter}
                          onChange={(e) => {
                            setBiasFilter(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        >
                          <option value="all">All Biases</option>
                          <option value="left">Left</option>
                          <option value="center-left">Center-Left</option>
                          <option value="center">Center</option>
                          <option value="center-right">Center-Right</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Filter by Status
                        </label>
                        <select
                          value={enabledFilter}
                          onChange={(e) => {
                            setEnabledFilter(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        >
                          <option value="all">All Feeds</option>
                          <option value="enabled">Enabled Only</option>
                          <option value="disabled">Disabled Only</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Feeds per Page
                        </label>
                        <select
                          value={feedsPerPage}
                          onChange={(e) => {
                            setFeedsPerPage(parseInt(e.target.value) || 25);
                            setCurrentPage(1);
                          }}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        >
                          <option value={25}>25 per page</option>
                          <option value={50}>50 per page</option>
                          <option value={100}>100 per page</option>
                          <option value={200}>200 per page</option>
                        </select>
                      </div>
                    </div>

                    {/* Filter Results Summary */}
                    <div className="mt-4 text-sm text-neutral-600">
                      Showing {filteredFeeds.length} of{" "}
                      {rssConfig.feeds?.length || 0} feeds
                      {searchTerm && ` matching "${searchTerm}"`}
                      {biasFilter !== "all" && ` with ${biasFilter} bias`}
                      {enabledFilter !== "all" && ` (${enabledFilter} only)`}
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Enabled
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Source Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          RSS URL
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Bias
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Max Articles
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Status
                        </th>
                        {rssConfigEditing && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {(rssConfigEditing
                        ? editingRssConfig?.feeds
                        : paginatedFeeds
                      )
                        ?.filter((feed) => feed && typeof feed === "object")
                        ?.map((feed, index) => (
                          <tr
                            key={`${feed.url}-${index}`}
                            className="hover:bg-neutral-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              {rssConfigEditing ? (
                                <input
                                  type="checkbox"
                                  checked={feed.enabled === true}
                                  onChange={(e) =>
                                    updateRssFeed(
                                      index,
                                      "enabled",
                                      e.target.checked
                                    )
                                  }
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                                />
                              ) : (
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    feed.enabled === true
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {feed.enabled === true ? "✅ On" : "❌ Off"}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {rssConfigEditing ? (
                                <input
                                  type="text"
                                  value={feed.name}
                                  onChange={(e) =>
                                    updateRssFeed(index, "name", e.target.value)
                                  }
                                  className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              ) : (
                                <div className="text-sm font-medium text-neutral-900">
                                  {feed.name}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {rssConfigEditing ? (
                                <input
                                  type="url"
                                  value={feed.url}
                                  onChange={(e) =>
                                    updateRssFeed(index, "url", e.target.value)
                                  }
                                  className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              ) : (
                                <div className="text-sm text-neutral-600 truncate max-w-xs">
                                  {feed.url}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {rssConfigEditing ? (
                                <select
                                  value={feed.bias}
                                  onChange={(e) =>
                                    updateRssFeed(index, "bias", e.target.value)
                                  }
                                  className="px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                  <option value="left">Left</option>
                                  <option value="center-left">
                                    Center-Left
                                  </option>
                                  <option value="center">Center</option>
                                  <option value="center-right">
                                    Center-Right
                                  </option>
                                  <option value="right">Right</option>
                                </select>
                              ) : (
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    feed.bias === "left"
                                      ? "bg-blue-100 text-blue-800"
                                      : feed.bias === "right"
                                        ? "bg-red-100 text-red-800"
                                        : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {feed.bias}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {rssConfigEditing ? (
                                <input
                                  type="number"
                                  min="1"
                                  max="50"
                                  value={feed.max_articles}
                                  onChange={(e) =>
                                    updateRssFeed(
                                      index,
                                      "max_articles",
                                      parseInt(e.target.value) || 10
                                    )
                                  }
                                  className="w-20 px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              ) : (
                                <span className="text-sm font-mono text-neutral-900">
                                  {feed.max_articles}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  feed.lastFetch &&
                                  new Date(feed.lastFetch) >
                                    new Date(Date.now() - 24 * 60 * 60 * 1000)
                                    ? "bg-green-100 text-green-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {feed.lastFetch
                                  ? `Updated ${new Date(feed.lastFetch).toLocaleDateString()}`
                                  : "Never"}
                              </span>
                            </td>
                            {rssConfigEditing && (
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => removeRssFeed(index)}
                                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                                >
                                  Remove
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {!rssConfigEditing && totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-neutral-700">
                      Page {currentPage} of {totalPages}(
                      {(currentPage - 1) * feedsPerPage + 1} -{" "}
                      {Math.min(
                        currentPage * feedsPerPage,
                        filteredFeeds.length
                      )}{" "}
                      of {filteredFeeds.length} feeds)
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm border border-neutral-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        Previous
                      </button>

                      {/* Page numbers */}
                      <div className="flex items-center space-x-1">
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                  currentPage === pageNum
                                    ? "bg-primary-600 text-white border-primary-600"
                                    : "border-neutral-300 hover:bg-neutral-50"
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                        )}
                      </div>

                      <button
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(totalPages, prev + 1)
                          )
                        }
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm border border-neutral-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                {/* Summary Stats */}
                <div className="mt-6 bg-blue-50 rounded-lg p-6">
                  <h4 className="text-sm font-medium text-blue-900 mb-4">
                    Summary Statistics
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {rssConfig.feeds?.filter((f) => f && f.enabled)
                          .length || 0}
                      </div>
                      <div className="text-blue-700">Active Feeds</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {rssConfig.feeds
                          ?.filter((f) => f && f.enabled)
                          .reduce(
                            (sum, f) => sum + (f.max_articles || 10),
                            0
                          ) || 0}
                      </div>
                      <div className="text-blue-700">Max Articles/Cycle</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {rssConfig.maxStories || 500}
                      </div>
                      <div className="text-blue-700">Max Stories Limit</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {rssConfig.updateFrequency || 60}
                      </div>
                      <div className="text-blue-700">
                        Minutes Between Updates
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {rssConfig.lastUpdate
                          ? new Date(rssConfig.lastUpdate).toLocaleTimeString()
                          : "Never"}
                      </div>
                      <div className="text-blue-700">Last Update</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-red-800 text-lg">
                Failed to load RSS configuration
              </div>
              <button
                onClick={loadRssConfig}
                className="mt-4 inline-flex items-center px-4 py-2 border border-primary-300 text-sm font-medium rounded text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                🔄 Retry Loading
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

RssManagementPage.propTypes = {
  user: PropTypes.shape({
    isAdmin: PropTypes.bool.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  logout: PropTypes.func.isRequired,
};

export default RssManagementPage;
